/** QuickHouse — Finitions 3D : textures procédurales (canvas) + textures importées.
 *  Chaque finition a un id, un libellé, une taille de motif en mètres et une fabrique. */
import * as THREE from 'three';

// ── Générateurs procéduraux (canvas 2D) ───────────────────────
function canvasTexture(size, draw) {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  draw(c.getContext('2d'), size);
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

const rnd = (() => { // pseudo-aléatoire déterministe (textures stables)
  let s = 42;
  return () => { s = (s * 16807) % 2147483647; return s / 2147483647; };
})();

function noise(ctx, size, count, colors, maxR = 1.6) {
  for (let i = 0; i < count; i++) {
    ctx.fillStyle = colors[Math.floor(rnd() * colors.length)];
    const r = 0.4 + rnd() * maxR;
    ctx.beginPath();
    ctx.arc(rnd() * size, rnd() * size, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

const makePlaster = () => canvasTexture(256, (ctx, s) => { // crépi
  ctx.fillStyle = '#ddd6c9';
  ctx.fillRect(0, 0, s, s);
  noise(ctx, s, 5200, ['#cfc7b8', '#e8e2d6', '#c4bcae', '#eee9de'], 1.8);
});

const makeTileFloor = () => canvasTexture(256, (ctx, s) => { // carrelage 60×60
  ctx.fillStyle = '#b8b4ac';
  ctx.fillRect(0, 0, s, s);
  ctx.fillStyle = '#dcdad4';
  ctx.fillRect(3, 3, s - 6, s - 6);
  noise(ctx, s, 700, ['#d2d0c9', '#e4e2dc'], 1.2);
});

const makeParquet = () => canvasTexture(512, (ctx, s) => { // parquet, lames 12 cm × 60
  const woods = ['#a5794f', '#b08657', '#96693f', '#ad8158', '#9c7047'];
  const rowH = s / 8; // 8 lames de 15 cm sur 1,2 m
  for (let r = 0; r < 8; r++) {
    const offset = (r % 3) * (s / 3);
    for (let cix = -1; cix < 3; cix++) {
      const x = cix * (s / 2) + offset;
      ctx.fillStyle = woods[Math.floor(rnd() * woods.length)];
      ctx.fillRect(x + 1, r * rowH + 1, s / 2 - 2, rowH - 2);
      // veinage
      ctx.strokeStyle = 'rgba(90, 60, 30, 0.25)';
      ctx.lineWidth = 1;
      for (let v = 0; v < 3; v++) {
        ctx.beginPath();
        ctx.moveTo(x + 4, r * rowH + 3 + rnd() * (rowH - 6));
        ctx.lineTo(x + s / 2 - 4, r * rowH + 3 + rnd() * (rowH - 6));
        ctx.stroke();
      }
    }
  }
});

const makeSlate = () => canvasTexture(512, (ctx, s) => { // ardoises 30×20 en quinconce
  ctx.fillStyle = '#2e3540';
  ctx.fillRect(0, 0, s, s);
  const w = s / 4;
  const h = s / 6;
  const shades = ['#3a4350', '#333c49', '#414b59', '#38414e'];
  for (let r = 0; r < 6; r++) {
    const off = (r % 2) * (w / 2);
    for (let cix = -1; cix < 5; cix++) {
      ctx.fillStyle = shades[Math.floor(rnd() * shades.length)];
      ctx.fillRect(cix * w + off + 1, r * h + 1, w - 2, h - 2);
    }
  }
});

const makeRoofTile = () => canvasTexture(512, (ctx, s) => { // tuiles canal
  ctx.fillStyle = '#8f4a2e';
  ctx.fillRect(0, 0, s, s);
  const w = s / 5;
  const h = s / 5;
  for (let r = 0; r < 5; r++) {
    const off = (r % 2) * (w / 2);
    for (let cix = -1; cix < 6; cix++) {
      const x = cix * w + off;
      const g = ctx.createLinearGradient(x, 0, x + w, 0);
      g.addColorStop(0, '#7c3b22');
      g.addColorStop(0.5, '#b35f3b');
      g.addColorStop(1, '#7c3b22');
      ctx.fillStyle = g;
      ctx.fillRect(x, r * h, w - 2, h - 3);
      ctx.fillStyle = 'rgba(60, 25, 12, 0.5)';
      ctx.fillRect(x, r * h + h - 5, w - 2, 3); // ombre du recouvrement
    }
  }
});

const makeGravel = () => canvasTexture(256, (ctx, s) => {
  ctx.fillStyle = '#9aa0a8';
  ctx.fillRect(0, 0, s, s);
  noise(ctx, s, 4200, ['#8a9099', '#a8aeb6', '#7e848c', '#b4bac2'], 2.2);
});

const makeGreen = () => canvasTexture(256, (ctx, s) => {
  ctx.fillStyle = '#55804a';
  ctx.fillRect(0, 0, s, s);
  noise(ctx, s, 4200, ['#4a7440', '#618c54', '#3f6a38', '#6d9a5e'], 2.0);
});

// ── Registre des finitions ────────────────────────────────────
// kind: 'wall' (faces de murs), 'floor' (sols de pièces), 'roof' (toits)
export const FINISHES = {
  'plaster': { label: 'Crépi', kind: 'wall', tile: 1.6, make: makePlaster },
  'paint-white': { label: 'Peinture blanche', kind: 'wall', color: '#f0eee7' },
  'paint-beige': { label: 'Peinture beige', kind: 'wall', color: '#e3d8c2' },
  'paint-grey': { label: 'Peinture grise', kind: 'wall', color: '#c8ccd2' },
  'paint-blue': { label: 'Peinture bleu gris', kind: 'wall', color: '#a8bccb' },
  'paint-terracotta': { label: 'Peinture terracotta', kind: 'wall', color: '#c07a5b' },
  'tile-floor': { label: 'Carrelage', kind: 'floor', tile: 0.6, make: makeTileFloor },
  'parquet': { label: 'Parquet', kind: 'floor', tile: 1.2, make: makeParquet },
  'slate-roof': { label: 'Trame ardoise', kind: 'roof', tile: 1.2, make: makeSlate },
  'tile-roof': { label: 'Trame tuile', kind: 'roof', tile: 1.0, make: makeRoofTile },
  'gravel': { label: 'Gravier', kind: 'roof', tile: 1.2, make: makeGravel },
  'green': { label: 'Végétal', kind: 'roof', tile: 1.4, make: makeGreen },
};

export function finishOptions(kind, textures = []) {
  const presets = Object.entries(FINISHES)
    .filter(([, f]) => f.kind === kind)
    .map(([id, f]) => ({ id, label: f.label }));
  const customs = textures.map((t) => ({ id: `tex:${t.id}`, label: `🖼 ${t.name}` }));
  return [...presets, ...customs];
}

// ── Menuiseries (portes, fenêtres, volets) ────────────────────
export const FRAME_COLORS = { pvc: '#f2f1ec', wood: '#7a5230', alu: '#878d93' };
export const FRAME_LABELS = { pvc: 'PVC', wood: 'Bois', alu: 'Aluminium' };

const makeWoodGrain = () => canvasTexture(256, (ctx, s) => {
  ctx.fillStyle = '#7a5230';
  ctx.fillRect(0, 0, s, s);
  ctx.strokeStyle = 'rgba(60, 38, 18, 0.35)';
  for (let i = 0; i < 26; i++) {
    ctx.lineWidth = 0.6 + rnd() * 1.6;
    ctx.beginPath();
    const y = rnd() * s;
    ctx.moveTo(0, y);
    ctx.bezierCurveTo(s / 3, y + (rnd() - 0.5) * 14, (2 * s) / 3, y + (rnd() - 0.5) * 14, s, y);
    ctx.stroke();
  }
});

const makeLouver = () => canvasTexture(128, (ctx, s) => { // persiennes
  for (let y = 0; y < s; y += 8) {
    ctx.fillStyle = '#00000000';
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.fillRect(0, y, s, 2);
    ctx.fillStyle = 'rgba(255,255,255,0.18)';
    ctx.fillRect(0, y + 2, s, 2);
  }
});

const makeSlats = () => canvasTexture(128, (ctx, s) => { // lames de volet roulant
  for (let y = 0; y < s; y += 10) {
    ctx.fillStyle = 'rgba(0,0,0,0.30)';
    ctx.fillRect(0, y, s, 2);
    ctx.fillStyle = 'rgba(255,255,255,0.14)';
    ctx.fillRect(0, y + 2, s, 1);
  }
});

const frameTexCache = new Map();
function overlayTex(kind) { // 'louver' | 'slats' | 'wood'
  if (!frameTexCache.has(kind)) {
    frameTexCache.set(kind, kind === 'louver' ? makeLouver() : kind === 'slats' ? makeSlats() : makeWoodGrain());
  }
  return frameTexCache.get(kind);
}

/** Matériaux de menuiserie partagés (mis en cache — ne pas disposer) */
const frameMatCache = new Map();
export function frameMaterial(mat, overlay = null) {
  const key = `${mat}|${overlay ?? ''}`;
  if (!frameMatCache.has(key)) {
    const m = new THREE.MeshStandardMaterial({
      color: overlay === 'wood-grain' ? '#ffffff' : FRAME_COLORS[mat] ?? '#f2f1ec',
      roughness: mat === 'alu' ? 0.45 : 0.75,
      metalness: mat === 'alu' ? 0.55 : 0.05,
    });
    if (mat === 'wood') {
      m.map = overlayTex('wood');
      m.color.set('#ffffff');
    }
    if (overlay === 'louver' || overlay === 'slats') {
      const t = overlayTex(overlay).clone();
      t.needsUpdate = true;
      t.repeat.set(1, 4);
      m.map = t;
      if (mat === 'wood') m.color.set('#8a6238');
      else m.color.set(FRAME_COLORS[mat]);
    }
    frameMatCache.set(key, m);
  }
  return frameMatCache.get(key);
}

// ── Volets : peintures texturées bois ─────────────────────────
export const SHUTTER_COLORS = {
  wood: { label: 'Bois naturel', color: '#8a6238' },
  white: { label: 'Blanc', color: '#e9e7e1' },
  grey: { label: 'Gris', color: '#878d93' },
  blue: { label: 'Bleu lavande', color: '#5a7593' },
  green: { label: 'Vert provence', color: '#5b7355' },
  red: { label: 'Rouge basque', color: '#8c3b30' },
  brown: { label: 'Marron foncé', color: '#54402c' },
};

/** Grain de bois neutre (teintable par la couleur du matériau), avec relief optionnel */
function makeNeutralGrain(overlay) {
  return canvasTexture(256, (ctx, s) => {
    ctx.fillStyle = '#d9d5cf';
    ctx.fillRect(0, 0, s, s);
    ctx.strokeStyle = 'rgba(70, 58, 45, 0.28)';
    for (let i = 0; i < 30; i++) {
      ctx.lineWidth = 0.5 + rnd() * 1.4;
      ctx.beginPath();
      const x = rnd() * s;
      ctx.moveTo(x, 0);
      ctx.bezierCurveTo(x + (rnd() - 0.5) * 12, s / 3, x + (rnd() - 0.5) * 12, (2 * s) / 3, x, s);
      ctx.stroke();
    }
    if (overlay === 'louver') {
      for (let y = 0; y < s; y += 14) {
        ctx.fillStyle = 'rgba(0,0,0,0.42)';
        ctx.fillRect(0, y, s, 4);
        ctx.fillStyle = 'rgba(255,255,255,0.20)';
        ctx.fillRect(0, y + 4, s, 3);
      }
    } else if (overlay === 'slats') {
      for (let y = 0; y < s; y += 18) {
        ctx.fillStyle = 'rgba(0,0,0,0.30)';
        ctx.fillRect(0, y, s, 3);
        ctx.fillStyle = 'rgba(255,255,255,0.14)';
        ctx.fillRect(0, y + 3, s, 2);
      }
    }
  });
}

const shutterMatCache = new Map();
/** Matériau de volet : peinture texturée bois (partagé/caché — ne pas disposer) */
export function shutterMaterial(colorKey, overlay = null) {
  const key = `${colorKey}|${overlay ?? ''}`;
  if (!shutterMatCache.has(key)) {
    const grainKey = `grain|${overlay ?? ''}`;
    if (!shutterMatCache.has(grainKey)) shutterMatCache.set(grainKey, makeNeutralGrain(overlay));
    const mat = new THREE.MeshStandardMaterial({
      color: SHUTTER_COLORS[colorKey]?.color ?? SHUTTER_COLORS.wood.color,
      roughness: 0.8,
      metalness: 0.02,
      map: shutterMatCache.get(grainKey),
    });
    shutterMatCache.set(key, mat);
  }
  return shutterMatCache.get(key);
}

// ── Charpente : poutres / poteaux ─────────────────────────────
const BEAM_COLORS = {
  wood: '#a5794f',
  'wood-dark': '#6f4e30',
  steel: '#3c4148',
  white: '#e9e9e4',
};

const beamMatCache = new Map();
/** Matériau d'un élément de charpente (partagé/caché — ne pas disposer) */
export function beamMaterial(mat = 'wood') {
  if (!beamMatCache.has(mat)) {
    const wood = mat === 'wood' || mat === 'wood-dark';
    if (wood && !beamMatCache.has('grain')) beamMatCache.set('grain', makeNeutralGrain(null));
    beamMatCache.set(mat, new THREE.MeshStandardMaterial({
      color: BEAM_COLORS[mat] ?? BEAM_COLORS.wood,
      roughness: mat === 'steel' ? 0.45 : 0.85,
      metalness: mat === 'steel' ? 0.45 : 0.02,
      map: wood ? beamMatCache.get('grain') : null,
    }));
  }
  return beamMatCache.get(mat);
}

// Verre : très transparent, avec de légers reflets diagonaux pour « montrer » la vitre.
// L'alphaMap module l'opacité : fond ≈ 0.18, bandes de reflet jusqu'à ≈ 0.32.
const makeGlassAlpha = () => {
  const tex = canvasTexture(256, (ctx, s) => {
    ctx.fillStyle = '#2e2e2e';
    ctx.fillRect(0, 0, s, s);
    ctx.save();
    ctx.translate(s / 2, s / 2);
    ctx.rotate(-Math.PI / 3.4);
    const bands = [
      [-s * 0.55, s * 0.16, '#3c3c3c'],
      [-s * 0.20, s * 0.07, '#4a4a4a'],
      [s * 0.16, s * 0.11, '#404040'],
      [s * 0.44, s * 0.05, '#525252'],
    ];
    for (const [x, w, col] of bands) {
      const g = ctx.createLinearGradient(x, 0, x + w, 0);
      g.addColorStop(0, '#2e2e2e');
      g.addColorStop(0.5, col);
      g.addColorStop(1, '#2e2e2e');
      ctx.fillStyle = g;
      ctx.fillRect(x, -s, w, s * 2);
    }
    ctx.restore();
  });
  tex.colorSpace = THREE.NoColorSpace; // carte de données, pas de correction sRGB
  return tex;
};

const glassMatCache = [];
export function glassMaterial() {
  if (!glassMatCache.length) {
    glassMatCache.push(new THREE.MeshStandardMaterial({
      color: '#cfe6f2', transparent: true, opacity: 1, alphaMap: makeGlassAlpha(),
      roughness: 0.08, metalness: 0.15, side: THREE.DoubleSide, depthWrite: false,
    }));
  }
  return glassMatCache[0];
}

// ── Cache et résolution ───────────────────────────────────────
const texCache = new Map(); // clé finition ou url → THREE.Texture
const loader = new THREE.TextureLoader();

/**
 * Résout une finition en { texture|null, color, tile } (texture partagée via cache).
 * `textures` = liste des textures importées du projet [{id, url}].
 */
export function resolveFinish(finish, textures = [], fallbackColor = '#e8e4da') {
  if (!finish) return { texture: null, color: fallbackColor, tile: 1 };
  if (finish.startsWith('tex:')) {
    const id = finish.slice(4);
    const entry = textures.find((t) => t.id === id);
    if (!entry) return { texture: null, color: fallbackColor, tile: 1 };
    if (!texCache.has(entry.url)) {
      const tex = loader.load(entry.url);
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = 4;
      texCache.set(entry.url, tex);
    }
    return { texture: texCache.get(entry.url), color: '#ffffff', tile: 1.5 };
  }
  const def = FINISHES[finish];
  if (!def) return { texture: null, color: fallbackColor, tile: 1 };
  if (!def.make) return { texture: null, color: def.color ?? fallbackColor, tile: 1 };
  if (!texCache.has(finish)) texCache.set(finish, def.make());
  return { texture: texCache.get(finish), color: '#ffffff', tile: def.tile ?? 1 };
}

/** Matériau standard pour une surface ShapeGeometry (UV en mètres → répétition mondiale) */
export function surfaceMaterial(finish, textures, fallbackColor, opts = {}) {
  const { texture, color, tile } = resolveFinish(finish, textures, fallbackColor);
  const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.9, metalness: 0, ...opts });
  if (texture) {
    const t = texture.clone();
    t.needsUpdate = true;
    t.repeat.set(1 / tile, 1 / tile);
    mat.map = t;
  }
  return mat;
}

/**
 * Matériaux des 6 faces d'une boîte de mur [len(m) × h(m) × ép] :
 * ordre BoxGeometry [+x, −x, +y, −y, +z, −z] — +z = face EXTÉRIEURE, −z = INTÉRIEURE.
 * Les textures sont ANCRÉES AU MONDE (anchorU/anchorY en mètres) : le motif est continu
 * d'un tronçon à l'autre (linteaux, allèges, plinthes, murs alignés, étages).
 * Les tranches (abouts, tableaux de fenêtres, sommet) reçoivent la finition extérieure.
 */
export function wallBoxMaterials(finishOut, finishIn, textures, lenM, hM, fallbackColor = '#e8e4da', anchorU = 0, anchorY = 0) {
  const face = (finish) => {
    const { texture, color, tile } = resolveFinish(finish, textures, fallbackColor);
    const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.9 });
    if (texture) {
      const t = texture.clone();
      t.needsUpdate = true;
      t.repeat.set(Math.max(lenM / tile, 0.02), Math.max(hM / tile, 0.02));
      t.offset.set(((anchorU / tile) % 1 + 1) % 1, ((anchorY / tile) % 1 + 1) % 1);
      mat.map = t;
    }
    return mat;
  };
  // Tranches : même finition que l'extérieur (tableaux de fenêtres propres, pas de gris brut)
  const sideSrc = resolveFinish(finishOut, textures, fallbackColor);
  const side = new THREE.MeshStandardMaterial({ color: sideSrc.color, roughness: 0.9 });
  if (sideSrc.texture) {
    const t = sideSrc.texture.clone();
    t.needsUpdate = true;
    t.repeat.set(0.35, Math.max(hM / sideSrc.tile, 0.05));
    side.map = t;
    side.color.set('#ffffff');
  }
  return [side, side, side, side, face(finishOut), face(finishIn)];
}
